import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Zap, 
  Users, 
  BarChart3, 
  Sparkles, 
  ArrowRight, 
  Check, 
  Play,
  ChevronDown,
  Menu,
  X,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube
} from 'lucide-react';
import { Button } from '../components/ui/button';

const FEATURES = [
  {
    icon: Calendar,
    title: 'Visual Content Calendar',
    description: 'Plan and organize your content with an intuitive drag-and-drop calendar. See your entire content strategy at a glance.'
  },
  {
    icon: Sparkles,
    title: 'AI-Powered Ideas',
    description: 'Never run out of content ideas. Our AI suggests trending topics, optimal hashtags, and engagement-boosting content.'
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Streamline workflows with approval processes, comments, and role-based permissions for your entire team.'
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics',
    description: 'Track performance across all platforms. Get actionable insights to optimize your social media strategy.'
  },
  {
    icon: Zap,
    title: 'Auto-Scheduling',
    description: 'Let AI determine the best times to post. Schedule content automatically for maximum engagement.'
  },
  {
    icon: Calendar,
    title: 'Multi-Platform Publishing',
    description: 'Publish to Facebook, Instagram, LinkedIn, Twitter, TikTok, and more from a single dashboard.'
  }
];

const PLANS = [
  {
    name: 'Base',
    price: 32,
    features: ['2 calendars', '2 users', '10 social accounts', 'Basic analytics', 'Content calendar'],
    popular: false
  },
  {
    name: 'Standard',
    price: 64,
    features: ['6 calendars', '6 users', '10 social accounts', 'Advanced analytics', 'Approval workflows', 'Team collaboration'],
    popular: true
  },
  {
    name: 'Advanced',
    price: 131,
    features: ['14 calendars', '14 users', '10 social accounts', 'AI content ideas', 'Custom branding', 'Priority support'],
    popular: false
  }
];

const TESTIMONIALS = [
  {
    name: 'Marcus Chen',
    role: 'Social Media Manager',
    company: 'TechStart Inc.',
    image: 'https://images.unsplash.com/photo-1755519024827-fd05075a7200?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBkaXZlcnNlJTIwaGVhZHNob3QlMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzcwODc5MzQ2fDA&ixlib=rb-4.1.0&q=85',
    quote: 'StelliSocial transformed how we manage social media. The calendar view and AI suggestions save us hours every week.'
  },
  {
    name: 'Sarah Jenkins',
    role: 'Creative Director',
    company: 'Brand Agency',
    image: 'https://images.unsplash.com/photo-1675186914580-94356f7c012c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBkaXZlcnNlJTIwaGVhZHNob3QlMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzcwODc5MzQ2fDA&ixlib=rb-4.1.0&q=85',
    quote: 'The approval workflows are a game-changer for agency work. Our clients love being able to review and approve content easily.'
  }
];

const PLATFORMS = [
  { name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { name: 'Twitter', icon: Twitter, color: '#1DA1F2' },
  { name: 'YouTube', icon: Youtube, color: '#FF0000' }
];

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/stellisocial-icon.svg" 
                alt="StelliSocial" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-bold text-xl text-slate-900 tracking-tight" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/product" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Product</Link>
              <Link to="/features" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Pricing</Link>
              <Link to="/resources" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Resources</Link>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="nav-login-btn">
                  Log in
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6" data-testid="nav-signup-btn">
                  Start Free Trial
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t border-slate-100 px-6 py-4"
          >
            <div className="flex flex-col gap-4">
              <Link to="/product" className="text-slate-600 py-2">Product</Link>
              <Link to="/features" className="text-slate-600 py-2">Features</Link>
              <Link to="/pricing" className="text-slate-600 py-2">Pricing</Link>
              <Link to="/resources" className="text-slate-600 py-2">Resources</Link>
              <hr className="my-2" />
              <Link to="/login" className="text-slate-600 py-2">Log in</Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full w-full">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered Social Media Management
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
                Schedule Smarter.
                <span className="text-emerald-500"> Engage Better.</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                The all-in-one platform to plan, create, schedule, and analyze your social media content. 
                Trusted by 50,000+ marketers worldwide.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 h-12 text-base font-semibold shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
                    data-testid="hero-cta-btn"
                  >
                    Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="rounded-full px-8 h-12 text-base font-medium border-slate-200 hover:bg-slate-50"
                    data-testid="hero-demo-btn"
                  >
                    <Play className="mr-2 h-4 w-4" /> Watch Demo
                  </Button>
                </Link>
              </div>

              {/* Social proof */}
              <div className="mt-12 flex items-center gap-8">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-amber-500">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">4.9/5 from 2,000+ reviews</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10">
                <img 
                  src="https://images.unsplash.com/photo-1758873268663-5a362616b5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxzb2NpYWwlMjBtZWRpYSUyMG1hcmtldGluZyUyMHRlYW0lMjB3b3JraW5nJTIwdG9nZXRoZXIlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzcwODc5MzQ2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Team collaborating on social media"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
              </div>

              {/* Floating cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl shadow-slate-900/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Engagement Up</p>
                    <p className="text-lg font-bold text-emerald-500">+127%</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-xl shadow-slate-900/10"
              >
                <div className="flex items-center gap-2">
                  {PLATFORMS.slice(0, 4).map((platform) => (
                    <div 
                      key={platform.id} 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${platform.color}20` }}
                    >
                      <platform.icon className="h-4 w-4" style={{ color: platform.color }} />
                    </div>
                  ))}
                  <span className="text-sm font-medium text-slate-600">+7 more</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-16 border-y border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-slate-500 mb-8">Publish to all major platforms from one dashboard</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {PLATFORMS.map((platform, i) => (
              <motion.div
                key={platform.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <platform.icon className="h-6 w-6" style={{ color: platform.color }} />
                <span className="font-medium">{platform.name}</span>
              </motion.div>
            ))}
            <span className="text-slate-400 font-medium">+ 6 more</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4"
            >
              Everything you need to dominate social
            </motion.h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From content planning to performance analytics, StelliSocial gives you all the tools to succeed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 border border-slate-100 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-500/5 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                  <feature.icon className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6 md:px-12 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4"
            >
              Simple, transparent pricing
            </motion.h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Start with a 14-day free trial. No credit card required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PLANS.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`relative rounded-2xl p-8 ${
                  plan.popular 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-slate-800 text-white'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-lime-400 text-slate-900 px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  <span className={plan.popular ? 'text-emerald-100' : 'text-slate-400'}>/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <Check className={`h-5 w-5 ${plan.popular ? 'text-white' : 'text-emerald-400'}`} />
                      <span className={plan.popular ? 'text-emerald-50' : 'text-slate-300'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button 
                    className={`w-full rounded-full h-12 font-semibold ${
                      plan.popular 
                        ? 'bg-white text-emerald-600 hover:bg-emerald-50' 
                        : 'bg-emerald-500 text-white hover:bg-emerald-600'
                    }`}
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  >
                    Get Started
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4"
            >
              Loved by marketers worldwide
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {TESTIMONIALS.map((testimonial, i) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm"
              >
                <div className="flex items-center gap-1 text-amber-500 mb-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-slate-700 text-lg leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-emerald-500">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4"
          >
            Ready to transform your social media?
          </motion.h2>
          <p className="text-lg text-emerald-100 mb-8">
            Join 50,000+ marketers using StelliSocial to grow their brands.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-10 h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
              data-testid="cta-signup-btn"
            >
              Start Your Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 md:px-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/stellisocial-icon-square-transparent.svg" 
                  alt="StelliSocial" 
                  className="w-8 h-8 rounded-lg"
                />
                <span className="font-bold text-xl text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm leading-relaxed">
                The all-in-one social media management platform for modern marketing teams.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white transition-colors">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white transition-colors">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white transition-colors">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white transition-colors">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} StelliSocial. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
